import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { publicApi, resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Calendar, MapPin, Truck, Package, Check } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

export default function RentalDetailPage() {
    const { machineId } = useParams();
    const navigate = useNavigate();
    const [machine, setMachine] = useState(null);
    const [blocked, setBlocked] = useState([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [quote, setQuote] = useState(null);
    const [fulfillment, setFulfillment] = useState("pickup");
    const [selectedAccessories, setSelectedAccessories] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [selectedImg, setSelectedImg] = useState(0);
    const [customer, setCustomer] = useState({
        first_name: "", last_name: "", company: "", email: "", phone: "",
        address: "", city: "", state: "", zip_code: ""
    });

    useEffect(() => {
        Promise.all([
            publicApi.getRentalMachine(machineId),
            publicApi.getRentalAvailability(machineId)
        ]).then(([mRes, aRes]) => {
            setMachine(mRes.data);
            setBlocked(aRes.data.blocked_dates || []);
        }).catch(console.error).finally(() => setLoading(false));
    }, [machineId]);

    useEffect(() => {
        if (startDate && endDate && machine) {
            publicApi.getRentalQuote({
                machine_id: machineId, start_date: startDate, end_date: endDate, accessory_ids: selectedAccessories
            }).then(res => setQuote(res.data)).catch(console.error);
        } else {
            setQuote(null);
        }
    }, [startDate, endDate, selectedAccessories, machineId]);

    const isDateBlocked = (dateStr) => {
        return blocked.some(b => dateStr >= b.start.slice(0, 10) && dateStr <= b.end.slice(0, 10));
    };

    const blockedRanges = useMemo(() => {
        return blocked.map(b => ({
            from: new Date(b.start.slice(0, 10) + "T12:00:00"),
            to: new Date(b.end.slice(0, 10) + "T12:00:00")
        }));
    }, [blocked]);

    const handleBook = async () => {
        if (!startDate || !endDate) return toast.error("Select rental dates");
        if (!customer.first_name || !customer.last_name || !customer.email || !customer.phone) return toast.error("Fill in all required fields");
        
        setSubmitting(true);
        try {
            const res = await publicApi.createBooking({
                machine_id: machineId,
                start_date: new Date(startDate).toISOString(),
                end_date: new Date(endDate).toISOString(),
                customer,
                fulfillment,
                accessory_ids: selectedAccessories,
                origin_url: window.location.origin
            });
            // Redirect to Stripe checkout
            window.location.href = res.data.checkout_url;
        } catch (err) {
            toast.error(err.response?.data?.detail || "Booking failed");
            setSubmitting(false);
        }
    };

    if (loading) return <div className="min-h-[70vh] flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
    if (!machine) return <div className="min-h-[70vh] flex items-center justify-center text-muted-foreground">Machine not found</div>;

    return (
        <div className="min-h-[70vh]" data-testid="rental-detail-page">
            <div className="bg-card border-b border-border">
                <div className="container mx-auto px-6 lg:px-12 py-8">
                    <button onClick={() => navigate("/rent")} className="flex items-center gap-2 text-muted-foreground hover:text-primary text-sm mb-4">
                        <ArrowLeft className="h-4 w-4" /> Back to Rentals
                    </button>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-foreground tracking-tight">
                        {machine.model} ({machine.year})
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">{machine.includes}</p>
                </div>
            </div>

            <div className="container mx-auto px-6 lg:px-12 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-8">
                        {/* Images Gallery */}
                        {machine.images?.length > 0 && (
                            <div>
                                <div className="aspect-[16/9] bg-card border border-border overflow-hidden mb-3 relative group">
                                    <img src={resolveImageUrl(machine.images[selectedImg || 0])} alt={machine.model} className="w-full h-full object-contain" />
                                    {machine.images.length > 1 && (
                                        <>
                                            <button onClick={() => setSelectedImg(prev => (prev || 0) <= 0 ? machine.images.length - 1 : (prev || 0) - 1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><ArrowLeft className="h-5 w-5" /></button>
                                            <button onClick={() => setSelectedImg(prev => (prev || 0) >= machine.images.length - 1 ? 0 : (prev || 0) + 1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><ArrowRight className="h-5 w-5" /></button>
                                            <span className="absolute bottom-3 left-3 bg-black/50 text-white text-xs font-mono px-2 py-1">{(selectedImg || 0) + 1} / {machine.images.length}</span>
                                        </>
                                    )}
                                </div>
                                {machine.images.length > 1 && (
                                    <div className="flex gap-2 overflow-x-auto">
                                        {machine.images.map((img, i) => (
                                            <img key={i} src={resolveImageUrl(img)} alt="" onClick={() => setSelectedImg(i)} className={`w-16 h-16 object-cover cursor-pointer ${(selectedImg || 0) === i ? "ring-2 ring-primary" : "opacity-60 hover:opacity-100"}`} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Videos */}
                        {machine.videos?.length > 0 && (
                            <div>
                                <h2 className="font-heading font-bold text-lg text-foreground mb-3">See It In Action</h2>
                                <div className="space-y-4">
                                    {machine.videos.map((v, i) => {
                                        const vidId = v.match(/(?:v=|youtu\.be\/)([^&?]+)/)?.[1];
                                        if (!vidId) return null;
                                        return (
                                            <div key={i} className="aspect-video bg-black">
                                                <iframe src={`https://www.youtube.com/embed/${vidId}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={`${machine.model} video ${i + 1}`} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <p className="text-muted-foreground text-sm">{machine.description}</p>

                        {/* Rates */}
                        <div>
                            <h2 className="font-heading font-bold text-lg text-foreground mb-3">Rental Rates</h2>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-card border border-border p-4 text-center">
                                    <p className="text-primary font-bold text-2xl">${machine.daily_rate}</p>
                                    <p className="text-muted-foreground text-xs uppercase mt-1">Per Day</p>
                                </div>
                                <div className="bg-card border border-border p-4 text-center">
                                    <p className="text-primary font-bold text-2xl">${machine.weekly_rate?.toLocaleString()}</p>
                                    <p className="text-muted-foreground text-xs uppercase mt-1">Per Week</p>
                                </div>
                                <div className="bg-card border border-border p-4 text-center">
                                    <p className="text-primary font-bold text-2xl">${machine.monthly_rate?.toLocaleString()}</p>
                                    <p className="text-muted-foreground text-xs uppercase mt-1">Per Month</p>
                                </div>
                            </div>
                            <p className="text-muted-foreground text-xs mt-2">Bookings of 4+ days automatically capped at the lower weekly rate.</p>
                        </div>

                        {/* Date Selection - Visual Calendar */}
                        <div className="bg-card border border-border p-6">
                            <h2 className="font-heading font-bold text-lg text-foreground mb-4 flex items-center gap-2">
                                <Calendar className="h-5 w-5 text-primary" /> Select Rental Dates
                            </h2>
                            <p className="text-muted-foreground text-xs mb-4">Click a start date, then click an end date. Red dates are unavailable.</p>
                            <div className="flex justify-center">
                                <DayPicker
                                    mode="range"
                                    numberOfMonths={2}
                                    selected={startDate && endDate ? { from: new Date(startDate + "T12:00:00"), to: new Date(endDate + "T12:00:00") } : startDate ? { from: new Date(startDate + "T12:00:00") } : undefined}
                                    onSelect={(range) => {
                                        if (range?.from) setStartDate(range.from.toISOString().split("T")[0]);
                                        else setStartDate("");
                                        if (range?.to) setEndDate(range.to.toISOString().split("T")[0]);
                                        else setEndDate("");
                                    }}
                                    disabled={[
                                        { before: new Date() },
                                        ...blockedRanges
                                    ]}
                                    modifiers={{ blocked: blockedRanges }}
                                    modifiersStyles={{ blocked: { backgroundColor: "#ef4444", color: "#fff", borderRadius: 0, opacity: 0.6 } }}
                                    styles={{
                                        months: { display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center" },
                                        caption: { color: "#f97316", fontWeight: "bold" },
                                        day: { color: "#e5e5e5", fontSize: "14px" },
                                        head_cell: { color: "#888", fontSize: "12px" },
                                        nav_button: { color: "#f97316" },
                                        day_selected: { backgroundColor: "#f97316", color: "#fff" },
                                        day_range_middle: { backgroundColor: "rgba(249,115,22,0.2)", color: "#fff" },
                                    }}
                                    fromMonth={new Date()}
                                />
                            </div>
                            {startDate && endDate && (
                                <div className="mt-4 flex items-center justify-center gap-4 text-sm">
                                    <span className="text-muted-foreground">From: <span className="text-foreground font-bold">{startDate}</span></span>
                                    <span className="text-muted-foreground">To: <span className="text-foreground font-bold">{endDate}</span></span>
                                </div>
                            )}
                            {blocked.length > 0 && (
                                <p className="text-yellow-500 text-xs mt-3 text-center">Red dates are unavailable due to existing bookings or maintenance.</p>
                            )}
                        </div>

                        {/* Accessories */}
                        {machine.accessories?.length > 0 && (
                            <div>
                                <h2 className="font-heading font-bold text-lg text-foreground mb-3">Optional Accessories</h2>
                                <div className="space-y-2">
                                    {machine.accessories.map(acc => (
                                        <label key={acc.id} className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${selectedAccessories.includes(acc.id) ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground"}`}>
                                            <input type="checkbox" checked={selectedAccessories.includes(acc.id)} onChange={() => setSelectedAccessories(prev => prev.includes(acc.id) ? prev.filter(id => id !== acc.id) : [...prev, acc.id])} />
                                            <div className="flex-1">
                                                <p className="text-foreground font-bold text-sm">{acc.name}</p>
                                                <p className="text-muted-foreground text-xs">{acc.description}</p>
                                            </div>
                                            <p className="text-primary font-mono text-sm">${acc.price_per_day}/day | ${acc.price_per_week}/wk</p>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Fulfillment */}
                        <div>
                            <h2 className="font-heading font-bold text-lg text-foreground mb-3">Pickup / Delivery</h2>
                            <div className="space-y-2">
                                <label className={`flex items-center gap-3 p-3 border cursor-pointer ${fulfillment === "pickup" ? "border-primary bg-primary/5" : "border-border"}`}>
                                    <input type="radio" name="fulfillment" checked={fulfillment === "pickup"} onChange={() => setFulfillment("pickup")} />
                                    <MapPin className="h-4 w-4 text-primary" />
                                    <div className="flex-1">
                                        <p className="text-foreground font-bold text-sm">Local Pickup — FREE</p>
                                        <p className="text-muted-foreground text-xs">Pick up from our Apex, NC facility</p>
                                    </div>
                                </label>
                                <label className={`flex items-center gap-3 p-3 border cursor-pointer ${fulfillment === "local_delivery" ? "border-primary bg-primary/5" : "border-border"}`}>
                                    <input type="radio" name="fulfillment" checked={fulfillment === "local_delivery"} onChange={() => setFulfillment("local_delivery")} />
                                    <Truck className="h-4 w-4 text-primary" />
                                    <div className="flex-1">
                                        <p className="text-foreground font-bold text-sm">Local Delivery (within 25 mi) — FREE</p>
                                        <p className="text-muted-foreground text-xs">Free delivery within 25 miles of Apex, NC</p>
                                    </div>
                                </label>
                                <label className={`flex items-center gap-3 p-3 border cursor-pointer ${fulfillment === "freight" ? "border-primary bg-primary/5" : "border-border"}`}>
                                    <input type="radio" name="fulfillment" checked={fulfillment === "freight"} onChange={() => setFulfillment("freight")} />
                                    <Package className="h-4 w-4 text-primary" />
                                    <div className="flex-1">
                                        <p className="text-foreground font-bold text-sm">Freight Shipping — At Customer Expense</p>
                                        <p className="text-muted-foreground text-xs">Shipping arranged at your expense. Rental days accumulate while in transit.</p>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Customer Info */}
                        <div className="bg-card border border-border p-6">
                            <h2 className="font-heading font-bold text-lg text-foreground mb-4">Your Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">First Name *</Label><Input value={customer.first_name} onChange={e => setCustomer({...customer, first_name: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" data-testid="rental-first-name" /></div>
                                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Last Name *</Label><Input value={customer.last_name} onChange={e => setCustomer({...customer, last_name: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" data-testid="rental-last-name" /></div>
                                <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Company</Label><Input value={customer.company} onChange={e => setCustomer({...customer, company: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Email *</Label><Input type="email" value={customer.email} onChange={e => setCustomer({...customer, email: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" data-testid="rental-email" /></div>
                                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone *</Label><Input value={customer.phone} onChange={e => setCustomer({...customer, phone: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" data-testid="rental-phone" /></div>
                                {fulfillment !== "pickup" && (
                                    <>
                                        <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Delivery Address</Label><Input value={customer.address} onChange={e => setCustomer({...customer, address: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                                        <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">City</Label><Input value={customer.city} onChange={e => setCustomer({...customer, city: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">State</Label><Input value={customer.state} onChange={e => setCustomer({...customer, state: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                                            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">ZIP</Label><Input value={customer.zip_code} onChange={e => setCustomer({...customer, zip_code: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar - Quote & Book */}
                    <div className="lg:col-span-1">
                        <div className="bg-card border border-border p-6 sticky top-24">
                            <h2 className="font-heading font-bold text-lg text-foreground mb-4">Rental Summary</h2>
                            <p className="text-foreground font-bold">{machine.model} ({machine.year})</p>
                            
                            {quote ? (
                                <div className="mt-4 space-y-3 text-sm">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Duration</span><span>{quote.days} day{quote.days > 1 ? "s" : ""}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Machine Rental</span><span>${quote.base_cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
                                    {quote.accessory_cost > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Accessories</span><span>${quote.accessory_cost.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>}
                                    <div className="border-t border-border pt-3 flex justify-between font-bold"><span>Total</span><span className="text-primary">${quote.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
                                    <div className="bg-primary/10 border border-primary/20 p-3">
                                        <div className="flex justify-between"><span className="text-muted-foreground text-xs">Deposit Due Now</span><span className="text-primary font-bold">${quote.deposit.toFixed(2)}</span></div>
                                        <div className="flex justify-between mt-1"><span className="text-muted-foreground text-xs">Balance Remaining</span><span className="font-bold">${quote.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span></div>
                                    </div>
                                    <p className="text-muted-foreground text-[10px]">$100 non-refundable reservation deposit. Balance payable online or at pickup.</p>
                                </div>
                            ) : (
                                <p className="text-muted-foreground text-sm mt-4">Select dates to see pricing</p>
                            )}

                            <Button
                                onClick={handleBook}
                                disabled={!quote || submitting}
                                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider py-6 mt-6"
                                data-testid="book-now-btn"
                            >
                                {submitting ? "Processing..." : `Reserve Now — $${quote?.deposit?.toFixed(2) || "100.00"} Deposit`}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
